"use client";

import { memo, useCallback, useId } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  useA2UIComponent,
  type A2UIComponentProps,
  type Types,
} from "@a2ui/react";

const ChevronDownIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const BeeOSMultipleChoice = memo(function BeeOSMultipleChoice({
  node,
  surfaceId,
}: A2UIComponentProps<Types.MultipleChoiceNode>) {
  const { resolveString, setValue, getValue } = useA2UIComponent(
    node,
    surfaceId,
  );
  const props = node.properties;
  const id = useId();
  const options = props.options ?? [];
  const selectionsPath = props.selections?.path;
  const extProps = props as typeof props & {
    label?: unknown;
    description?: unknown;
  };
  const label = resolveString(
    extProps.label as Parameters<typeof resolveString>[0],
  );
  const description = resolveString(
    extProps.description as Parameters<typeof resolveString>[0],
  );

  const currentValue = selectionsPath
    ? (() => {
        const raw = getValue(selectionsPath);
        if (Array.isArray(raw) && raw.length > 0) return String(raw[0]);
        if (typeof raw === "string") return raw;
        return undefined;
      })()
    : (() => {
        const arr = (props.selections as { literalArray?: string[] } | undefined)?.literalArray;
        return Array.isArray(arr) && arr.length > 0 ? arr[0] : undefined;
      })();

  const handleValueChange = useCallback(
    (value: string) => {
      if (selectionsPath) setValue(selectionsPath, [value]);
    },
    [selectionsPath, setValue],
  );

  const hostStyle: React.CSSProperties =
    node.weight !== undefined
      ? ({ "--weight": node.weight } as React.CSSProperties)
      : {};

  return (
    <div className="a2ui-multiplechoice" style={hostStyle}>
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium leading-none text-foreground"
          >
            {label}
          </label>
        )}
        <SelectPrimitive.Root
          value={currentValue}
          onValueChange={handleValueChange}
        >
          <SelectPrimitive.Trigger
            id={id}
            className="flex h-10 w-full items-center justify-between rounded-xl border border-border/40 bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-0 focus:border-primary/30 disabled:cursor-not-allowed disabled:opacity-50 transition-all shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.2)] focus:shadow-[inset_0_1px_2px_rgba(0,0,0,0.04),0_0_0_3px_rgba(0,0,0,0.03)] dark:focus:shadow-[inset_0_1px_2px_rgba(0,0,0,0.15),0_0_0_3px_rgba(255,255,255,0.04)] [&>span]:line-clamp-1"
          >
            <SelectPrimitive.Value
              placeholder={description || "Select an option..."}
            />
            <SelectPrimitive.Icon className="ml-2 opacity-50">
              <ChevronDownIcon />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              className="relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-xl border border-border/30 bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1"
              position="popper"
            >
              <SelectPrimitive.Viewport className="p-1 h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]">
                {options.map((option, index) => {
                  const optionLabel = resolveString(option.label);
                  const optionValue =
                    typeof option.value === "string"
                      ? option.value
                      : (resolveString(
                          option.value as Parameters<typeof resolveString>[0],
                        ) ?? `option-${index}`);
                  return (
                    <SelectPrimitive.Item
                      key={optionValue}
                      value={optionValue}
                      className="relative flex w-full cursor-default select-none items-center rounded-lg py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                    >
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        <SelectPrimitive.ItemIndicator>
                          <CheckIcon />
                        </SelectPrimitive.ItemIndicator>
                      </span>
                      <SelectPrimitive.ItemText>
                        {optionLabel}
                      </SelectPrimitive.ItemText>
                    </SelectPrimitive.Item>
                  );
                })}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </div>
    </div>
  );
});
